import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Package, MapPin, Clock, CheckCircle2, Truck,
  ArrowRight, FileSearch, AlertCircle
} from "lucide-react";
import { PageHeader } from "../components/sections/Shared";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { SAMPLE_TRACKING, COMPANY } from "../data/mock";

export default function Tracking() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const search = (e, override) => {
    e?.preventDefault();
    const raw = override != null ? override : code;
    const key = raw.trim().toLowerCase();
    setError("");
    setResult(null);
    if (!key) return;
    // try direct match or with 518- prefix
    const found =
      SAMPLE_TRACKING[key] ||
      SAMPLE_TRACKING[key.replace(/^#?/, "")] ||
      Object.values(SAMPLE_TRACKING).find((r) => r.id.toLowerCase() === key);
    if (!found) {
      setError(`No order found for "${raw}". Try 518-2049 or 518-2050.`);
      return;
    }
    setResult(found);
  };

  return (
    <>
      <Seo
        title="Track Your Delivery | 518 Courier"
        description="Track your 518 Courier delivery live. Enter your order number for real-time status, ETA and proof of delivery. Call 518-954-4701 for help."
        path="/tracking"
      />
      <PageHeader
        eyebrow="Live tracking"
        title="Track your"
        accent="package."
        description="Enter your order number to see real-time status, ETA and proof of delivery. Demo tracking data — not a real shipment. Try 518-2049 or 518-2050."
        image="https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzgwMjUzNDA5fDA&ixlib=rb-4.1.0&q=85"
      />

      <section className="py-12 md:py-16 bg-slate-50">
        <div className="container-518 max-w-3xl">
          <form
            onSubmit={search}
            className="bg-white rounded-2xl shadow-lg border border-slate-200 p-3 flex flex-col sm:flex-row gap-3"
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter order #, e.g. 518-2049"
                className="h-13 h-14 pl-12 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
              />
            </div>
            <Button type="submit" className="btn-lift bg-[#f59321] hover:bg-[#e07f10] text-white rounded-xl h-14 px-7 font-semibold">
              Track
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="text-slate-500 font-medium">Try:</span>
            {["518-2049", "518-2050"].map((s) => (
              <button
                key={s}
                onClick={() => { setCode(s); search(null, s); }}
                className="px-2.5 py-1 rounded-full bg-white border border-slate-200 text-[#0f2847] hover:border-[#f59321] hover:text-[#f59321] font-semibold transition"
              >
                {s}
              </button>
            ))}
          </div>

          {error && (
            <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
              <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
              <div className="text-sm">{error}</div>
            </div>
          )}

          {result && (
            <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Demo disclaimer banner */}
              <div data-testid="tracking-demo-banner" className="flex items-start gap-2 px-6 md:px-8 py-3 bg-amber-50 border-b border-amber-200 text-amber-800">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="text-xs font-semibold">Demo tracking data — not a real shipment. This is sample data for illustration only.</div>
              </div>
              {/* status header */}
              <div className="px-6 md:px-8 py-6 bg-[#0f2847] text-white">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#f59321]">
                      Order {result.id}
                    </div>
                    <div className="font-display text-2xl md:text-3xl font-extrabold mt-1">
                      {result.status}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-300 font-semibold">
                      {result.statusKey === "delivered" ? "Delivered in" : "ETA"}
                    </div>
                    <div className="font-display text-2xl font-extrabold text-[#f59321]">{result.eta}</div>
                  </div>
                </div>
                <div className="mt-5 h-2 bg-white/15 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#f59321] rounded-full transition-all"
                    style={{ width: `${result.progress}%` }}
                  />
                </div>
              </div>

              <div className="px-6 md:px-8 py-6 grid sm:grid-cols-2 gap-5 border-b border-slate-200">
                <Info icon={MapPin} label="From" value={result.from} />
                <Info icon={MapPin} label="To" value={result.to} />
                <Info icon={Package} label="Service" value={result.service} />
                <Info icon={Truck} label="Driver" value={result.driver} />
              </div>

              <div className="px-6 md:px-8 py-6">
                <h3 className="font-display font-bold text-[#0f2847] text-lg mb-5">Timeline</h3>
                <ol className="relative">
                  <div className="absolute left-[15px] top-2 bottom-2 w-[2px] bg-slate-200" />
                  {result.events.map((e, i) => (
                    <li key={i} className="relative pl-12 pb-6 last:pb-0">
                      <span
                        className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center ring-4 ring-white ${
                          e.active
                            ? "bg-[#f59321] text-white"
                            : e.done
                            ? "bg-emerald-500 text-white"
                            : "bg-slate-200 text-slate-400"
                        }`}
                      >
                        {e.active ? <Truck className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                      </span>
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div className="font-display font-bold text-[#0f2847]">{e.label}</div>
                        <div className="text-xs font-semibold text-slate-500">{e.date} · {e.time}</div>
                      </div>
                      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{e.desc}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {!result && !error && (
            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {[
                { icon: FileSearch, title: "Find your order", text: "Order # is in your confirmation email or SMS." },
                { icon: Clock, title: "Live updates", text: "Status refreshes the moment dispatch logs an event." },
                { icon: CheckCircle2, title: "Proof of delivery", text: "Signature, photo and timestamp on every drop." }
              ].map(({ icon: Icon, title, text }, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5">
                  <div className="w-10 h-10 rounded-xl bg-[#f59321]/10 text-[#f59321] flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="font-display font-bold text-[#0f2847] mb-1">{title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 text-center text-sm text-slate-500">
            Can't find your order?{" "}
            <a href={`tel:${COMPANY.phone.replace(/[^0-9]/g, "")}`} className="text-[#f59321] font-semibold hover:underline">
              Call dispatch · {COMPANY.phone}
            </a>
            {" or "}
            <Link to="/contact" className="text-[#0f2847] font-semibold hover:underline">contact us</Link>.
          </div>
        </div>
      </section>
    </>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-slate-100 text-[#0f2847] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</div>
        <div className="text-sm font-semibold text-[#0f2847] mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}
